import createNamedContext from 'utils/createNamedContext';
import { memo, PropsWithChildren, useCallback, useContext, useMemo, useState, useRef } from 'react';
import { produce } from 'immer';
import { ParamsWithSet, Params, BaseProps, ItemV5 } from './types';
import { infiniteQueryItemListV5, queryItemListV5 } from './api';
import { useQuery } from '@tanstack/react-query';
import { forEach, get, omit, map, slice } from 'lodash-es';
import { getSpuInfoBySelectMap, useSelectControl } from '@knowledge/common/helpers/sku';
import { searchInItemScope } from '@knowledge/common/helpers/search';
import { SupplementalKnowledgeAiReplyKnowledge } from 'services/api/supplementKnowledge';
import { filterByBindItem } from './components/QuickSelect';
import { queryItemList } from '../ItemSelectV2/api';

const SearchContext = createNamedContext<ParamsWithSet>('ItemSelectV5SearchContext', null!);

export const SearchProvider = memo(
  (
    props: PropsWithChildren<{
      defaultParams?: Partial<Params>;
    }>,
  ) => {
    const { children, defaultParams } = props;
    const [params, _setParams] = useState<Params>({
      keywordType: 1,
      keyword: '',
      pageNo: 1,
      pageSize: 10,
      platformStoreScope: [],
      tagId: [],
      categoryUniqueId: [],
      ...defaultParams,
    });

    const setParams = useCallback((params: Partial<Params>) => {
      _setParams(
        produce((draft) => {
          draft = { ...draft, ...params };
          if (!params.pageNo) {
            draft.pageNo = 1;
          }
          return draft;
        }),
      );
    }, []);
    const providerValue = useMemo(() => {
      return {
        ...params,
        setParams,
      };
    }, [params, setParams]);
    return <SearchContext.Provider value={providerValue}>{children}</SearchContext.Provider>;
  },
);
SearchProvider.displayName = 'ItemSelectV5SearchProvider';

export const useSearchContext = () => {
  const value = useContext(SearchContext);
  if (!value) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return value;
};

type SkuFoldStatusProps = {
  skuFoldStatus: 'fold' | 'unfold';
  setSkuFoldStatus: (status: 'fold' | 'unfold') => void;
};
type OnlyCheckedProps = {
  onlyChecked: boolean;
  setOnlyChecked: (checked: boolean) => void;
  onlyCheckedLoading: boolean;
  setOnlyCheckedLoading: (loading: boolean) => void;
};
// baseProps context
const BaseContext = createNamedContext<BaseProps & Params & SkuFoldStatusProps & OnlyCheckedProps>(
  'ItemSelectV5BaseContext',
  null!,
);
export const BaseContextProvider = memo((props: PropsWithChildren<BaseProps>) => {
  const { children, skuDisabled = false, defaultSkuFoldStatus = 'fold', ...baseProps } = props;
  const searchContext = useSearchContext();
  const [skuFoldStatus, setSkuFoldStatus] = useState<'fold' | 'unfold'>(
    defaultSkuFoldStatus || 'fold',
  );
  const [onlyChecked, setOnlyChecked] = useState(false);
  const [onlyCheckedLoading, setOnlyCheckedLoading] = useState(false);
  return (
    <BaseContext.Provider
      value={{
        ...baseProps,
        ...searchContext,
        skuFoldStatus,
        skuDisabled,
        setSkuFoldStatus,
        onlyChecked,
        setOnlyChecked,
        onlyCheckedLoading,
        setOnlyCheckedLoading,
      }}
    >
      {children}
    </BaseContext.Provider>
  );
});
BaseContextProvider.displayName = 'ItemSelectV5BaseContextProvider';
export const useBaseContext = () => {
  const value = useContext(BaseContext);
  if (!value) {
    throw new Error('useBaseContext must be used within a BaseContextProvider');
  }
  return value;
};

// item context
type ItemContextProps = {
  loadedItems: ItemV5[];
  items: ItemV5[];
  fetchItemLoading: boolean;
  total: number;
  bindItem?: SupplementalKnowledgeAiReplyKnowledge['bindItem'];
  resetItems: () => void;
  addToLoadedItems: (items: ItemV5[]) => void;
  selectAll: () => Promise<void>;
  reverseSelect: () => Promise<void>;
  toggleOnlyChecked: (checked: boolean) => void;
} & ReturnType<typeof useSelectControl>['2'] & {
    spuSelectMap: ReturnType<typeof useSelectControl>['0'];
  };
const ItemContext = createNamedContext<ItemContextProps>('ItemSelectV5ItemContext', null!);
export const useItemContext = () => {
  const value = useContext(ItemContext);
  if (!value) {
    throw new Error('useItemContext must be used within a ItemContextProvider');
  }
  return value;
};
export const ItemContextProvider = (props: {
  children: React.ReactNode | ((items: ItemV5[], resetItems: () => void) => React.ReactNode);
  bindItem?: SupplementalKnowledgeAiReplyKnowledge['bindItem'];
}) => {
  const { children, bindItem } = props;
  const { setParams, ...restParams } = useSearchContext();
  const { onlyChecked, setOnlyCheckedLoading, setOnlyChecked } = useBaseContext();
  const [loadedItemsMap, setLoadedItemsMap] = useState<Record<string, ItemV5>>({});
  const addToLoadedItems = useCallback(
    (items: ItemV5[]) => {
      setLoadedItemsMap(
        produce((draft) => {
          forEach(items, (item) => {
            draft[item.internalItemId] = item;
          });
        }),
      );
    },
    [setLoadedItemsMap],
  );
  const loadedItems = useMemo(() => {
    return Object.values(loadedItemsMap);
  }, [loadedItemsMap]);
  const selectedItemsRef = useRef<ItemV5[]>([]);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['itemList', restParams, onlyChecked, bindItem] as const,
    queryFn: ({ queryKey }) => {
      const [, params, onlyChecked] = queryKey;
      if (onlyChecked) {
        return {
          itemList: slice(
            selectedItemsRef.current,
            (params.pageNo - 1) * params.pageSize,
            params.pageNo * params.pageSize,
          ),
          pageInfo: {
            totalCount: selectedItemsRef.current.length,
          },
        };
      } else if (bindItem) {
        return queryItemList({
          ...params,
          keyword: bindItem.map((_) => _.spuId).join(','),
          keywordType: 2,
          storeType: 0,
        }).then((result) => {
          result.itemList = filterByBindItem(result?.itemList || [], bindItem);
          addToLoadedItems(get(result, 'itemList', []));
          return result;
        });
      } else {
        return queryItemListV5(params).then((result) => {
          addToLoadedItems(get(result, 'itemList', []));
          return result;
        });
      }
    },
  });
  const items = useMemo(() => {
    return get(data, 'itemList', []);
  }, [data]);
  const [
    spuSelectMap,
    _, // eslint-disable-line
    { toggleSku, toggleSpu, toggleBatch, getBatchStatus, cancelSelect, toggleSkus },
  ] = useSelectControl(loadedItems, undefined, {
    keepPrevSelected: true,
  });
  const selectedItems = useMemo(() => {
    return getSpuInfoBySelectMap(loadedItems, spuSelectMap);
  }, [spuSelectMap, loadedItems]);
  const resetItems = useCallback(() => {
    setLoadedItemsMap({});
    setParams({
      pageNo: 1,
    });
    refetch();
  }, [setParams, refetch]);

  const selectAll = useCallback(async () => {
    if (onlyChecked) {
      return toggleBatch(map(selectedItemsRef.current, 'internalItemId'), true);
    } else {
      return infiniteQueryItemListV5(omit({ ...restParams }, ['pageNo', 'pageSize'])).then(
        (res) => {
          addToLoadedItems(res);
          setTimeout(() => {
            toggleBatch(map(res, 'internalItemId'), true);
          });
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyChecked, restParams, addToLoadedItems]);
  const reverseSelect = useCallback(async () => {
    if (onlyChecked) {
      return toggleBatch(map(selectedItemsRef.current, 'internalItemId'), false);
    } else {
      searchInItemScope(selectedItems, {
        keyword: restParams.keyword,
        keywordType: restParams.keywordType,
        storeScopes: restParams.platformStoreScope,
        tagIds: restParams.tagId,
        categoryUniqueIds: restParams.categoryUniqueId,
      }).then((res) => {
        console.log(res);
        toggleBatch(map(res, 'internalItemId'), false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restParams, onlyChecked, selectedItems]);

  const toggleOnlyChecked = useCallback(
    (checked: boolean) => {
      if (checked) {
        setOnlyCheckedLoading(true);
        searchInItemScope(selectedItems, {
          keyword: restParams.keyword,
          keywordType: restParams.keywordType,
          storeScopes: restParams.platformStoreScope,
          tagIds: restParams.tagId,
          categoryUniqueIds: restParams.categoryUniqueId,
        }).then((res) => {
          selectedItemsRef.current = res;
          setOnlyCheckedLoading(false);
          setParams({
            pageNo: 1,
          });
          setOnlyChecked(true);
        });
      } else {
        setOnlyChecked(false);
        setParams({
          pageNo: 1,
        });
      }
    },
    [selectedItems, restParams, setOnlyCheckedLoading, setOnlyChecked, setParams],
  );

  return (
    <ItemContext.Provider
      value={{
        loadedItems,
        items,
        total: get(data, 'pageInfo.totalCount', 0),
        bindItem,
        spuSelectMap,
        toggleSku,
        toggleSkus,
        toggleSpu,
        toggleBatch,
        getBatchStatus,
        cancelSelect,
        fetchItemLoading: isLoading,
        resetItems,
        addToLoadedItems,
        selectAll,
        reverseSelect,
        toggleOnlyChecked,
      }}
    >
      {typeof children === 'function' ? children(selectedItems, resetItems) : children}
    </ItemContext.Provider>
  );
};
